import React, { FC, useContext } from 'react'
import {
  Grid,
  Card,
  Typography,
  Box,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material'
import { DataGrid, GridRenderCellParams } from '@mui/x-data-grid'
import { Edit as EditIcon, Add as AddIcon, Search as SearchIcon, Delete as DeleteIcon } from '@mui/icons-material'
import { Article } from 'shared/Article'
import { FormDrawerComponent } from 'shared/FormDrawerComponent'
import { useVehicleMedia } from './useVehicleMedia'
import { VehicleMediaForm } from './VehicleMediaForm'
import { ClientContext } from '../../../../utils/clientContext'

export const VehicleMedia: FC = () => {
  const clientContext = useContext(ClientContext)
  const clientId = clientContext?.clientId || ''

  const {
    vehicleMediaList,
    isLoading,
    // Filter states
    make,
    setMake,
    model,
    setModel,
    year,
    setYear,
    trim,
    setTrim,
    // Drawer states
    isAddDrawerOpen,
    setIsAddDrawerOpen,
    isEditDrawerOpen,
    setIsEditDrawerOpen,
    editingRecord,
    // Delete modal states
    isDeleteDialogOpen,
    deletingRecord,
    // Actions
    handleSearch,
    handleEdit,
    handleDelete,
    handleDeleteConfirm,
    handleDeleteCancel,
    upsertMutation
  } = useVehicleMedia(clientId)

  const columns = [
    {
      field: 'make',
      headerName: 'Make',
      flex: 0.3,
    },
    {
      field: 'model',
      headerName: 'Model',
      flex: 0.3,
    },
    {
      field: 'trim',
      headerName: 'Trim',
      flex: 0.3,
    },
    {
      field: 'year',
      headerName: 'Year',
      flex: 0.2,
    },
    {
      field: 'data',
      headerName: 'Data',
      flex: 0.5,
    },
    {
      field: 'created_at',
      headerName: 'Created At',
      flex: 0.3,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.2,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => handleEdit(params.row)}
            sx={{ color: 'primary.main' }}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDelete(params.row)}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
  ]

  const handleAddSave = (data: any) => {
    upsertMutation.mutate(data)
  }

  const handleEditSave = (data: any) => {
    if (editingRecord) {
      upsertMutation.mutate({ ...data })
    }
  }

  return (
    <Article
      heading="Vehicle Media"
      headerHasBottomBorder={false}
    >
      <Grid container spacing={3}>
        <Grid size={{xs:12}}>
          <Card sx={{ p: 3, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{xs:12, sm:6, md:3}}>
                <TextField
                  fullWidth
                  label="Make"
                  value={make}
                  onChange={(e) => setMake(e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid size={{xs:12, sm:6, md:3}}>
                <TextField
                  fullWidth
                  label="Model"
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid size={{xs:12, sm:6, md:2}}>
                <TextField
                  fullWidth
                  label="Year"
                  type="number"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid size={{xs:12, sm:6, md:2}}>
                <TextField
                  fullWidth
                  label="Trim"
                  value={trim}
                  onChange={(e) => setTrim(e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid size={{xs:12, sm:6, md:2}}>
                <Button
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={handleSearch}
                  disabled={isLoading}
                  fullWidth
                >
                  {isLoading ? 'Searching...' : 'Search'}
                </Button>
              </Grid>
            </Grid>
          </Card>
        </Grid>

        <Grid size={{xs:12}}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">
              Vehicle Media Records
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setIsAddDrawerOpen(true)}
            >
              Add Vehicle Media
            </Button>
          </Box>

          <Card>
            <DataGrid
              rows={vehicleMediaList || []}
              columns={columns}
              paginationModel={{page: 0, pageSize: 25}}
              pageSizeOptions={[25, 50, 100]}
              loading={isLoading}
              autoHeight
              getRowId={(row) => row.id || `${row.make}-${row.model}-${row.trim}-${row.year}-${Date.now()}`}
            />
          </Card>
        </Grid>
      </Grid>

      {/* Add Vehicle Media Drawer */}
      <FormDrawerComponent
        isDrawerOpen={isAddDrawerOpen}
        setIsDrawerOpen={setIsAddDrawerOpen}
        header="Add Vehicle Media"
        handleSave={handleAddSave}
        form={VehicleMediaForm}
        defaultValues={{
          id: null,
          make: '',
          model: '',
          trim: '',
          year: new Date().getFullYear(),
          data: '',
        }}
      />

      {/* Edit Vehicle Media Drawer */}
      <FormDrawerComponent
        isDrawerOpen={isEditDrawerOpen}
        setIsDrawerOpen={setIsEditDrawerOpen}
        header="Edit Vehicle Media"
        handleSave={handleEditSave}
        form={VehicleMediaForm}
        defaultValues={editingRecord}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Delete Vehicle Media</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you would like to delete this record?
          </DialogContentText>
          {deletingRecord && (
            <Box sx={{ mt: 2, p: 2, borderRadius: 1 }}>
              <Typography variant="body2"><strong>Make:</strong> {deletingRecord.make}</Typography>
              <Typography variant="body2"><strong>Model:</strong> {deletingRecord.model}</Typography>
              <Typography variant="body2"><strong>Trim:</strong> {deletingRecord.trim}</Typography>
              <Typography variant="body2"><strong>Year:</strong> {deletingRecord.year}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            autoFocus
            variant="contained"
            color="secondary"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Article>
  )
}
